/* Level a QUIET recording before speech recognition.
 *
 * What it is for. A phone left on the table across the room records speech
 * 30–45 dB below a normal file. Nothing in that file is broken, but both of
 * our ASR lanes were losing it: Silero VAD marks faint speech as silence and
 * whisper's own log-mel front end keeps such input near its floor. Measured on
 * a real 10-minute family recording (2026-09-02): 247 words recognised as-is,
 * 1174 after a plain gain — same model, same file. LibriSpeech numbers with
 * exact ground truth are in infra/whisper/BENCHMARK-quiet-input-2026-09-02.md.
 *
 * What it is NOT. Not a denoiser. Denoising a recording this faint erases the
 * voices along with the noise (DeepFilterNet at its default attenuation took a
 * faint clip from 3.4% to 100% WER, 2026-06-25) — which is exactly what the
 * bug reporter's earlier attempts had done to her own file. This module only
 * ever ADDS gain, and only when the input is clearly below normal level.
 *
 * Contract (shared with infra/whisper/transcribe.sh, which mirrors the same
 * measurement with ffmpeg so the two lanes level identically):
 *   level   = 90th percentile of per-100 ms frame RMS, in dBFS. The top decile
 *             of frames is where speech lives; pauses and room tone drop out
 *             of the estimate without any VAD.
 *   gain    = TARGET_DB − level, applied only when level < TARGET_DB − DEADBAND_DB
 *             (a normal file is left byte-identical), clamped to MAX_GAIN_DB.
 *   limiter = soft knee above ±0.5 (tanh), so an amplified click cannot wrap.
 *   silence = level below SILENCE_DB is "no recording here" — amplifying room
 *             hiss to speech level just hands whisper 30 s windows of noise to
 *             hallucinate over, so those are left alone.
 *
 * Loaded by the whisper worker through /workers/asr-level (dynamic import; the
 * file assigns self.__asrLevel like asr-quality-guard.js) and usable from a
 * page or from Node (module.exports) — the benchmark drives THIS file.
 */
(function (root) {
    'use strict';

    var SAMPLE_RATE = 16000;
    var FRAME = 1600;              // 100 ms at 16 kHz
    var TARGET_DB = -20;           // p90 frame RMS of normally recorded speech
    var DEADBAND_DB = 6;           // inputs within 6 dB of target are untouched
    var MAX_GAIN_DB = 40;
    var SILENCE_DB = -70;          // below this there is no speech to raise
    var MIN_FRAMES = 5;            // < 0.5 s: nothing to measure

    function frameLevelsDb(pcm) {
        var n = Math.floor(pcm.length / FRAME);
        var out = new Float32Array(n);
        for (var f = 0; f < n; f++) {
            var off = f * FRAME, acc = 0;
            for (var i = 0; i < FRAME; i++) { var v = pcm[off + i]; acc += v * v; }
            out[f] = 10 * Math.log10(acc / FRAME + 1e-18);
        }
        return out;
    }

    function percentile(sorted, p) {
        if (!sorted.length) return null;
        var idx = Math.min(sorted.length - 1, Math.max(0, Math.floor(p * (sorted.length - 1))));
        return sorted[idx];
    }

    function peakDb(pcm) {
        var m = 0;
        for (var i = 0; i < pcm.length; i++) { var a = pcm[i] < 0 ? -pcm[i] : pcm[i]; if (a > m) m = a; }
        return 20 * Math.log10(m + 1e-9);
    }

    /* Measure only — never touches the buffer. */
    function measure(pcm) {
        var levels = frameLevelsDb(pcm);
        if (levels.length < MIN_FRAMES) {
            return { levelDb: null, peakDb: null, gainDb: 0, frames: levels.length, reason: 'too_short' };
        }
        var sorted = Array.prototype.slice.call(levels).sort(function (a, b) { return a - b; });
        var p90 = percentile(sorted, 0.9);
        var res = { levelDb: +p90.toFixed(1), peakDb: +peakDb(pcm).toFixed(1), gainDb: 0, frames: levels.length, reason: 'normal' };
        if (p90 < SILENCE_DB) { res.reason = 'silence'; return res; }
        if (p90 < TARGET_DB - DEADBAND_DB) {
            res.gainDb = +Math.min(MAX_GAIN_DB, TARGET_DB - p90).toFixed(1);
            res.reason = 'quiet';
        }
        return res;
    }

    /* Apply gain in place with a soft knee above ±0.5. Returns the share of
       samples that went through the knee — the amount of limiting that
       happened, for telemetry. */
    function apply(pcm, gainDb) {
        var g = Math.pow(10, gainDb / 20), limited = 0;
        for (var i = 0; i < pcm.length; i++) {
            var v = pcm[i] * g, a = v < 0 ? -v : v;
            if (a > 0.5) {
                a = 0.5 + 0.5 * Math.tanh((a - 0.5) / 0.5);
                v = v < 0 ? -a : a;
                limited++;
            }
            pcm[i] = v;
        }
        return { limitedFrac: pcm.length ? limited / pcm.length : 0 };
    }

    /* Measure, and raise the buffer in place when it is quiet. */
    function level(pcm) {
        var m = measure(pcm);
        m.applied = false;
        m.limitedFrac = 0;
        if (m.gainDb > 0) {
            /* Kept unrounded: one click in ten minutes is 1e-6 of the samples,
               and rounding here reported it as "nothing was limited". */
            m.limitedFrac = apply(pcm, m.gainDb).limitedFrac;
            m.applied = true;
        }
        return m;
    }

    var api = {
        measure: measure, apply: apply, level: level,
        SAMPLE_RATE: SAMPLE_RATE, FRAME: FRAME, TARGET_DB: TARGET_DB, DEADBAND_DB: DEADBAND_DB,
        MAX_GAIN_DB: MAX_GAIN_DB, SILENCE_DB: SILENCE_DB,
    };
    root.__asrLevel = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this));
